// ─────────────────────────────────────────────────────────────────────────────
// Masse salariale RÉELLE prorata temporis — fonctions pures, sans Firestore.
//
// Contrairement à scenario.ts (montants théoriques dérivés de la grille), on
// part ici des TAUX RÉELS datés (rate_history). Un changement de taux en cours
// d'année découpe l'année en segments ; chaque segment pèse au prorata de sa
// durée en jours :
//
//   montant_segment = taux × heures/sem × semaines/an × (jours_segment / jours_année)
//   total annuel    = Σ segments
//
// La fenêtre d'emploi (embauche / départ en cours d'année) borne les segments.
// Les jours antérieurs au premier taux connu sont exclus du total et signalés
// via coverageDays / unpriced.
// ─────────────────────────────────────────────────────────────────────────────

import { round2 } from './projection'

export type RatePeriodInput = {
  hourly_rate: number
  effective_date: string // ISO 'YYYY-MM-DD'
}

export type PayrollSegment = {
  start: string // ISO, borne incluse
  end: string // ISO, borne incluse
  hourly_rate: number
  days: number
  amount: number
}

export type AnnualPayroll = {
  total: number
  segments: PayrollSegment[]
  // Jours de la fenêtre d'emploi effectivement couverts par un taux connu.
  coverageDays: number
  // Jours de la fenêtre d'emploi ∩ période (indépendamment du prix) — sert au
  // prorata des avantages, qui suivent l'emploi et non le taux. 0 si fenêtre vide.
  windowDays: number
  // Aucun taux connu sur toute la fenêtre (ou fenêtre vide) : total = 0.
  unpriced: boolean
}

// Jours UTC pour éviter les surprises de fuseau/DST avec des dates 'YYYY-MM-DD'.
const MS_PER_DAY = 24 * 60 * 60 * 1000

function toUtc(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

function toIso(utcMs: number): string {
  return new Date(utcMs).toISOString().slice(0, 10)
}

// Nombre de jours entre deux dates ISO, bornes incluses.
function inclusiveDays(startIso: string, endIso: string): number {
  return Math.round((toUtc(endIso) - toUtc(startIso)) / MS_PER_DAY) + 1
}

export function daysInYear(year: number): number {
  return inclusiveDays(`${year}-01-01`, `${year}-12-31`)
}

export function annualRealPayroll(input: {
  year: number
  rates: RatePeriodInput[] // ordre quelconque
  weeklyHours: number // EmployeeWork.weekly_hours ?? class.base_hours_per_week
  weeksPerYear: number // class.weeks_per_year (52)
  hireDate: string | null
  departureDate: string | null
  // Sous-période demandée (ISO 'YYYY-MM-DD', bornes incluses). Par défaut
  // l'année civile complète ⇒ résultat identique au comportement d'origine.
  // Toujours bornée à l'année (period ⊆ year) : le dénominateur de prorata
  // reste daysInYear(year), donc chaque jour couvert pèse 1/365 (ou 1/366) d'un
  // salaire annuel — sommer les jours d'une sous-période = coût prorata exact.
  periodStart?: string
  periodEnd?: string
}): AnnualPayroll {
  const { year, weeklyHours, weeksPerYear, hireDate, departureDate } = input
  const yearStart = `${year}-01-01`
  const yearEnd = `${year}-12-31`

  // Période clippée à l'année (defense in depth) : max(periodStart, yearStart),
  // min(periodEnd, yearEnd).
  const periodStart =
    input.periodStart && input.periodStart > yearStart ? input.periodStart : yearStart
  const periodEnd =
    input.periodEnd && input.periodEnd < yearEnd ? input.periodEnd : yearEnd

  // Fenêtre d'emploi ∩ période (bornes incluses).
  const windowStart = hireDate && hireDate > periodStart ? hireDate : periodStart
  const windowEnd = departureDate && departureDate < periodEnd ? departureDate : periodEnd
  if (windowStart > windowEnd) {
    return { total: 0, segments: [], coverageDays: 0, windowDays: 0, unpriced: true }
  }
  const windowDays = inclusiveDays(windowStart, windowEnd)

  const rates = [...input.rates].sort((a, b) =>
    a.effective_date.localeCompare(b.effective_date),
  )

  // Taux en vigueur au début de fenêtre = dernier changement antérieur ou égal.
  // À effective_date égale, le dernier saisi (fin du tri stable) l'emporte.
  let startRate: number | null = null
  for (const r of rates) {
    if (r.effective_date <= windowStart) startRate = r.hourly_rate
  }

  // Points de coupe : début de fenêtre + chaque changement dans la fenêtre.
  // Une Map déduplique les changements multiples à la même date (dernier gagne).
  const cuts = new Map<string, number | null>()
  cuts.set(windowStart, startRate)
  for (const r of rates) {
    if (r.effective_date > windowStart && r.effective_date <= windowEnd) {
      cuts.set(r.effective_date, r.hourly_rate)
    }
  }
  const cutDates = [...cuts.keys()].sort()

  const denom = daysInYear(year)
  const annualBase = weeklyHours * weeksPerYear
  const segments: PayrollSegment[] = []
  let total = 0
  let coverageDays = 0

  for (let i = 0; i < cutDates.length; i++) {
    const start = cutDates[i]
    const end =
      i + 1 < cutDates.length ? toIso(toUtc(cutDates[i + 1]) - MS_PER_DAY) : windowEnd
    const rate = cuts.get(start) ?? null
    if (rate === null) continue // avant le premier taux connu : non couvert
    const days = inclusiveDays(start, end)
    const amount = round2(rate * annualBase * (days / denom))
    segments.push({ start, end, hourly_rate: rate, days, amount })
    total += amount
    coverageDays += days
  }

  return {
    total: round2(total),
    segments,
    coverageDays,
    windowDays,
    unpriced: segments.length === 0,
  }
}

// Montant d'un boni = base annuelle (salaire réel prorata) × pourcentage
// (fraction, ex. 0.10). `pct` null ⇒ aucun boni saisi ⇒ 0.
export function bonusAmount(annualBase: number, pct: number | null): number {
  if (pct == null) return 0
  return round2(annualBase * pct)
}

// Écart entre le taux réel et le taux de la grille (classe/échelon de l'année).
export function rateGapToGrid(
  realRate: number,
  gridRate: number,
): { delta: number; pct: number | null } {
  const delta = round2(realRate - gridRate)
  return { delta, pct: gridRate > 0 ? delta / gridRate : null }
}

// Agrégat de masse salariale réelle sur un groupe (équipe ou entreprise).
export type TeamPayrollMember = {
  id: string // email
  payroll: AnnualPayroll
}

export type TeamPayrollTotals = {
  total: number
  // Membres sans aucun taux connu — exclus du total, à signaler à l'affichage.
  unpriced: string[]
}

export function teamRealPayroll(members: TeamPayrollMember[]): TeamPayrollTotals {
  let total = 0
  const unpriced: string[] = []
  for (const m of members) {
    if (m.payroll.unpriced) unpriced.push(m.id)
    else total += m.payroll.total
  }
  return { total: round2(total), unpriced }
}
